/**
 * Daily reading chart component that displays water consumption data in a bar chart format.
 * Features internationalized date labels and consumption values.
 */

import { Bar } from "react-chartjs-2";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

/**
 * DailyReadingChart component that renders a bar chart of daily water consumption data.
 * Includes translated date labels and formatted consumption values.
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Chart data object containing labels and datasets
 * @returns {JSX.Element} The rendered DailyReadingChart component
 */
export const DailyReadingChart = ({ data }) => {
  const { t, i18n } = useTranslation();

  /**
   * Formats date strings to localized format
   * @param {string} dateString - Date string in ISO format
   * @returns {string} Formatted date string
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language || undefined);
  };

  // Create translated data
  const translatedData = {
    ...data,
    labels: data.labels.map((label) => formatDate(label)),
    datasets: data.datasets.map((dataset) => ({
      ...dataset,
      label: t("daily_consumption"),
    })),
  };

  /**
   * Chart configuration options including scales, plugins, and tooltips
   */
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
        grace: "0%",
        ticks: {
          precision: 0,
        },
        title: {
          display: true,
          text: t("daily_consumption_difference"),
        },
      },
      x: {
        title: {
          display: true,
          text: t("date"),
        },
      },
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: true,
        text: t("daily_reading"),
        font: {
          size: 16,
          weight: "bold",
        },
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            return tooltipItems[0].label;
          },
          label: (context) => {
            const value = context.parsed.y;
            return `${t("daily_consumption")}: ${value.toLocaleString()}`;
          },
        },
      },
    },
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Bar data={translatedData} options={options} />
    </div>
  );
};

// PropTypes validation for DailyReadingChart component
DailyReadingChart.propTypes = {
  data: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string).isRequired,
    datasets: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.number).isRequired,
        backgroundColor: PropTypes.string,
        borderColor: PropTypes.string,
        borderWidth: PropTypes.number,
      })
    ).isRequired,
  }).isRequired,
  onToggleChart: PropTypes.func.isRequired,
};

export default DailyReadingChart;
